import React from 'react'
import { useMutation } from '@apollo/client'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteIcon from '@mui/icons-material/Delete'
import { useCallback, useMemo, useState } from 'react'
import { Alert, Button } from 'ui/baseComponents'
import Loading from 'ui/components/Loading'
import AdminPageHeader from 'ui/screens/Admin/AdminPageHeader'
import { formatDateString } from 'utilities/time'
import { useCampaignList, LIST_CAMPAIGNS } from '../../graphql/campaignQueries'
import {
  CREATE_CAMPAIGN,
  DELETE_CAMPAIGN,
  CLONE_CAMPAIGN,
} from '../../graphql/campaignMutations'
import { useTagList } from '../../graphql/tagQueries'
import CampaignForm from './CampaignForm'

const CampaignsTab = () => {
  const navigate = useNavigate()
  const { loading, error, data } = useCampaignList()
  const { data: tagsData } = useTagList()
  const [showForm, setShowForm] = useState(false)
  const [deleteConfirmId, setDeleteConfirmId] = useState(null)
  const [cloneDialog, setCloneDialog] = useState(null)
  const [cloneName, setCloneName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  const refetchConfig = {
    refetchQueries: [{ query: LIST_CAMPAIGNS, variables: { limit: 100 } }],
  }

  const [createCampaign] = useMutation(CREATE_CAMPAIGN, refetchConfig)
  const [deleteCampaign] = useMutation(DELETE_CAMPAIGN, refetchConfig)
  const [cloneCampaign] = useMutation(CLONE_CAMPAIGN, refetchConfig)

  const campaigns = useMemo(() => data?.campaignList?.campaigns || [], [data])
  const tags = useMemo(() => tagsData?.tags || [], [tagsData])
  const tagMap = Object.fromEntries(tags.map((t) => [t.id, t.tag]))

  const filteredCampaigns = useMemo(() => {
    if (!searchQuery.trim()) return campaigns
    const query = searchQuery.toLowerCase()
    return campaigns.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.id.toLowerCase().includes(query) ||
        (tagMap[c.tagId] || '').toLowerCase().includes(query) ||
        (c.description || '').toLowerCase().includes(query),
    )
  }, [campaigns, searchQuery, tagMap])

  const handleCreate = useCallback(
    async (formData) => {
      const input = {
        name: formData.name,
        tagId: formData.tagId,
      }
      if (formData.description) input.description = formData.description
      if (formData.priority) input.priority = parseInt(formData.priority, 10)
      if (formData.startDate) input.startDate = formData.startDate
      if (formData.endDate) input.endDate = formData.endDate
      if (formData.active !== undefined) input.active = formData.active

      const result = await createCampaign({ variables: { input } })
      setShowForm(false)
      const newId = result.data?.createCampaign?.campaign?.id
      if (newId) {
        navigate(`/admin/outreach/campaigns/${newId}/edit`)
      }
    },
    [createCampaign, navigate],
  )

  const handleDelete = useCallback(
    async (campaignId) => {
      await deleteCampaign({ variables: { input: { id: campaignId } } })
      setDeleteConfirmId(null)
    },
    [deleteCampaign],
  )

  const handleClone = useCallback(async () => {
    if (!cloneName.trim() || !cloneDialog) return
    await cloneCampaign({
      variables: {
        input: { campaignId: cloneDialog.id, name: cloneName.trim() },
      },
    })
    setCloneDialog(null)
    setCloneName('')
  }, [cloneDialog, cloneName, cloneCampaign])

  if (loading && !data) return <Loading />
  if (error)
    return (
      <Alert severity="error">Failed to load campaigns: {error.message}</Alert>
    )

  return (
    <Box>
      <AdminPageHeader>
        <Typography variant="h6">
          Campaigns ({filteredCampaigns.length})
        </Typography>
        <Button
          type="primary"
          size="small"
          onClick={() => setShowForm(true)}
          startIcon={<AddIcon />}
        >
          New Campaign
        </Button>
      </AdminPageHeader>

      <Paper elevation={1} sx={{ p: 2 }}>
        <TextField
          placeholder="Search campaigns by name, ID, tag, or description..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        />

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tag</TableCell>
                <TableCell>Active</TableCell>
                <TableCell>Priority</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Content</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCampaigns.map((campaign) => (
                <TableRow
                  key={campaign.id}
                  hover
                  onClick={() =>
                    navigate(`/admin/outreach/campaigns/${campaign.id}`)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight="medium">
                      {campaign.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {campaign.id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={tagMap[campaign.tagId] || campaign.tagId}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.active ? 'Active' : 'Inactive'}
                      size="small"
                      color={campaign.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>{campaign.priority}</TableCell>
                  <TableCell>
                    {campaign.startDate
                      ? formatDateString({
                          dateString: campaign.startDate,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {campaign.endDate
                      ? formatDateString({
                          dateString: campaign.endDate,
                          format: 'MMM DD, YYYY',
                        })
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {campaign.campaignContent?.length || 0} steps
                  </TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation()
                        setCloneDialog(campaign)
                        setCloneName(`${campaign.name} (copy)`)
                      }}
                      title="Clone"
                    >
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={(e) => {
                        e.stopPropagation()
                        setDeleteConfirmId(campaign.id)
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <CampaignForm
        open={showForm}
        onClose={() => setShowForm(false)}
        onSubmit={handleCreate}
        initialValues={null}
        tags={tags}
      />

      <Dialog open={!!deleteConfirmId} onClose={() => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Campaign</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure? This will permanently delete this campaign and all
            associated content.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setDeleteConfirmId(null)}>
            Cancel
          </Button>
          <Button
            type="primary"
            onClick={() => handleDelete(deleteConfirmId)}
            sx={{ backgroundColor: 'error.main' }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!cloneDialog}
        onClose={() => setCloneDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Clone Campaign</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Create a copy of &ldquo;{cloneDialog?.name}&rdquo; with all its
            content.
          </Typography>
          <TextField
            label="New Campaign Name"
            value={cloneName}
            onChange={(e) => setCloneName(e.target.value)}
            fullWidth
            size="small"
          />
        </DialogContent>
        <DialogActions>
          <Button type="secondary" onClick={() => setCloneDialog(null)}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleClone}>
            Clone
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CampaignsTab
